/**
 * Subscription Analytics Tab
 * Dashboard with stats, charts, and recent events
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, RefreshCw, Users, CreditCard, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import clsx from 'clsx';
import { useI18n } from '@/i18n';

// Types
interface SubscriptionSummary {
  total_users: number;
  total_subscribers: number;
  trial_users: number;
  active_users: number;
  expired_users: number;
  cancelled_users: number;
  trial_to_paid_rate: number;
  churn_rate: number;
}

interface StatusDistribution {
  none: number;
  trial: number;
  active: number;
  expired: number;
  cancelled: number;
}

interface ProductDistribution {
  [product_id: string]: number;
}

interface DailyStat {
  date: string;
  new_trials: number;
  conversions: number;
  cancellations: number;
  new_subscriptions: number;
}

interface RecentEvent {
  user_id: string;
  display_name: string | null;
  event_type: string;
  previous_status: string | null;
  new_status: string | null;
  product_id: string | null;
  created_at: string;
}

interface SubscriptionAnalytics {
  summary: SubscriptionSummary;
  status_distribution: StatusDistribution;
  product_distribution: ProductDistribution;
  daily_stats: DailyStat[];
  recent_events: RecentEvent[];
}

// API functions
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

async function fetchAnalytics(startDate?: string, endDate?: string): Promise<SubscriptionAnalytics> {
  const token = localStorage.getItem('admin_token');
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);

  const response = await fetch(
    `${API_BASE}/v1/subscription/admin/analytics?${params}`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error('Failed to fetch analytics');
  return response.json();
}

// Helper components
function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  trendLabel,
  color = 'primary',
}: {
  title: string;
  value: string | number;
  icon: typeof Users;
  trend?: number;
  trendLabel?: string;
  color?: 'primary' | 'green' | 'yellow' | 'red' | 'blue';
}) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={clsx(
            'flex items-center text-sm font-medium',
            trend >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {trend >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{title}</div>
        {trendLabel && <div className="text-xs text-gray-400 mt-1">{trendLabel}</div>}
      </div>
    </div>
  );
}

function StatusBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{value} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full', color)} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

export function SubscriptionAnalyticsTab() {
  const { t } = useI18n();
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const getDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case '7d':
        return { start: new Date(now.setDate(now.getDate() - 7)).toISOString() };
      case '30d':
        return { start: new Date(now.setDate(now.getDate() - 30)).toISOString() };
      case '90d':
        return { start: new Date(now.setDate(now.getDate() - 90)).toISOString() };
      default:
        return {};
    }
  };

  const { start } = getDateRange();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-analytics', dateRange],
    queryFn: () => fetchAnalytics(start),
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEventBadge = (eventType: string) => {
    const badges: Record<string, { labelKey: string; color: string }> = {
      'trial_started': { labelKey: 'subscriptionPage.analytics.eventTypes.trialStarted', color: 'bg-blue-100 text-blue-700' },
      'subscription_created': { labelKey: 'subscriptionPage.analytics.eventTypes.subscribed', color: 'bg-green-100 text-green-700' },
      'subscription_renewed': { labelKey: 'subscriptionPage.analytics.eventTypes.renewed', color: 'bg-green-100 text-green-700' },
      'subscription_cancelled': { labelKey: 'subscriptionPage.analytics.eventTypes.cancelled', color: 'bg-red-100 text-red-700' },
      'subscription_expired': { labelKey: 'subscriptionPage.analytics.eventTypes.expired', color: 'bg-yellow-100 text-yellow-700' },
      'admin_override': { labelKey: 'subscriptionPage.analytics.eventTypes.admin', color: 'bg-purple-100 text-purple-700' },
    };
    const badge = badges[eventType];
    const label = badge ? t(badge.labelKey) : eventType;
    const color = badge ? badge.color : 'bg-gray-100 text-gray-700';
    return (
      <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', color)}>
        {label}
      </span>
    );
  };

  return (
    <div>
      {/* Header with Date Range and Refresh */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                dateRange === range
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {range === 'all' ? t('subscriptionPage.analytics.dateRanges.allTime') :
               range === '7d' ? t('subscriptionPage.analytics.dateRanges.days7') :
               range === '30d' ? t('subscriptionPage.analytics.dateRanges.days30') :
               t('subscriptionPage.analytics.dateRanges.days90')}
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('subscriptionPage.analytics.refresh')}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="text-red-800 font-semibold">{t('subscriptionPage.analytics.errorLoading')}</h3>
            <p className="text-red-600 text-sm">{(error as Error).message}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Analytics Content */}
      {data && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <StatCard
              title={t('subscriptionPage.analytics.totalUsers')}
              value={data.summary.total_users.toLocaleString()}
              icon={Users}
              color="primary"
            />
            <StatCard
              title={t('subscriptionPage.analytics.activeSubscribers')}
              value={data.summary.active_users.toLocaleString()}
              icon={CreditCard}
              color="green"
            />
            <StatCard
              title={t('subscriptionPage.analytics.trialToPaidRate')}
              value={`${data.summary.trial_to_paid_rate.toFixed(1)}%`}
              icon={TrendingUp}
              color="blue"
            />
            <StatCard
              title={t('subscriptionPage.analytics.churnRate')}
              value={`${data.summary.churn_rate.toFixed(1)}%`}
              icon={TrendingDown}
              color="red"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Status Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.statusDistribution')}</h3>
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.free')}
                value={data.status_distribution.none}
                total={data.summary.total_users}
                color="bg-gray-400"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.trial')}
                value={data.status_distribution.trial}
                total={data.summary.total_users}
                color="bg-blue-500"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.active')}
                value={data.status_distribution.active}
                total={data.summary.total_users}
                color="bg-green-500"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.expired')}
                value={data.status_distribution.expired}
                total={data.summary.total_users}
                color="bg-yellow-500"
              />
              <StatusBar
                label={t('subscriptionPage.analytics.statusLabels.cancelled')}
                value={data.status_distribution.cancelled}
                total={data.summary.total_users}
                color="bg-red-500"
              />
            </div>

            {/* Product Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.productDistribution')}</h3>
              {Object.keys(data.product_distribution).length === 0 ? (
                <p className="text-gray-500 text-sm">{t('subscriptionPage.analytics.noProductData')}</p>
              ) : (
                Object.entries(data.product_distribution).map(([product, count]) => (
                  <StatusBar
                    key={product}
                    label={product}
                    value={count}
                    total={data.summary.active_users}
                    color="bg-primary-500"
                  />
                ))
              )}
            </div>
          </div>

          {/* Daily Stats */}
          {data.daily_stats.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.dailyTrends')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.date')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.newTrials')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.conversions')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.newSubscriptions')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">{t('subscriptionPage.analytics.cancellations')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.daily_stats.slice(0, 14).map((stat) => (
                      <tr key={stat.date} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">{formatDate(stat.date)}</td>
                        <td className="px-4 py-2 text-sm text-right text-blue-600">{stat.new_trials}</td>
                        <td className="px-4 py-2 text-sm text-right text-green-600">{stat.conversions}</td>
                        <td className="px-4 py-2 text-sm text-right text-primary-600">{stat.new_subscriptions}</td>
                        <td className="px-4 py-2 text-sm text-right text-red-600">{stat.cancellations}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Events */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.analytics.recentEvents')}</h3>
            {data.recent_events.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('subscriptionPage.analytics.noRecentEvents')}</p>
            ) : (
              <div className="space-y-3">
                {data.recent_events.map((event, index) => (
                  <div
                    key={`${event.user_id}-${event.created_at}-${index}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {event.display_name || event.user_id.slice(0, 8)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {event.product_id && <span className="mr-2">{event.product_id}</span>}
                          {formatDateTime(event.created_at)}
                        </div>
                      </div>
                    </div>
                    {getEventBadge(event.event_type)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
